// The four "Select:" shortcuts the review screens share — All, None, all
// high-confidence, all high + medium.
//
// The bulk grid and the single-ref table hold their ticks in different shapes
// (grid: ref → field → boolean, over derived defaults; single-ref: field →
// boolean, computed once), so the state walk stays in each screen. What is
// shared is this: which rows a mode SHOULD tick, and — for the single-ref
// table — which rows are held back from a blanket select whatever the mode.

import type { CheckedReason } from '../../services/bulkEnrichFields';

export type SelectionMode = 'all' | 'none' | 'high' | 'highMedium';

export const SELECTION_MODES: { mode: SelectionMode; label: string }[] = [
  { mode: 'all', label: 'All' },
  { mode: 'none', label: 'None' },
  { mode: 'high', label: 'All high-confidence' },
  { mode: 'highMedium', label: 'All high + medium' },
];

/** Does a confidence satisfy the mode? (`all`/`none` ignore confidence.) */
export function modeWantsConfidence(mode: SelectionMode, confidence: string | undefined): boolean {
  const c = (confidence ?? 'medium').toLowerCase();
  switch (mode) {
    case 'all':
      return true;
    case 'none':
      return false;
    case 'high':
      return c === 'high';
    case 'highMedium':
      return c === 'high' || c === 'medium';
  }
}

/**
 * A row the single-ref table is about to bulk-select.
 *
 * `heldBack` is the reason a blanket select must NOT tick it — the guards
 * `defaultAcceptForSuggestion` applies when the dialog opens. A shortcut that
 * ignored them would, on one click, write a slug that matched no lookup, a
 * calibre that matched no catalogue entry, or a description over one a person
 * wrote. The reviewer can still tick any of them by hand; the shortcut just
 * will not do it for them.
 */
export interface SelectionRow {
  key: string;
  confidence: string | undefined;
  heldBack?: CheckedReason;
}

export interface SelectionResult {
  accepted: Record<string, boolean>;
  reasons: Record<string, CheckedReason>;
  /** How many rows the mode ticked. */
  selected: number;
  /** How many rows it refused to tick because they are held back. */
  heldBack: number;
}

/**
 * Apply a shortcut to every row, returning a COMPLETE map — every proposed row
 * gets an explicit entry, so the reviewer's choice fully replaces the defaults
 * rather than leaving unmentioned rows on whatever they were.
 */
export function applySelectionMode(
  rows: readonly SelectionRow[],
  mode: SelectionMode,
): SelectionResult {
  const accepted: Record<string, boolean> = {};
  const reasons: Record<string, CheckedReason> = {};
  let selected = 0;
  let heldBack = 0;

  for (const row of rows) {
    if (row.heldBack && mode !== 'none') {
      accepted[row.key] = false;
      reasons[row.key] = row.heldBack;
      heldBack += 1;
      continue;
    }
    const want = modeWantsConfidence(mode, row.confidence);
    accepted[row.key] = want;
    reasons[row.key] = want ? 'checked' : mode === 'none' ? 'bulkCleared' : 'belowThreshold';
    if (want) selected += 1;
  }

  return { accepted, reasons, selected, heldBack };
}
